import { ReactNode } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { ViewportType } from './state';

export type ViewportPreviewProps = {
  children: ReactNode;
  viewport: ViewportType;
};

export const ViewportPreview = ({ children, viewport }: ViewportPreviewProps) => {
  return (
    <Container data-viewport={viewport}>
      <Boundary>
        <FormContainer>{children}</FormContainer>
      </Boundary>
    </Container>
  );
};

const Container = styled(motion.div).attrs({
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, transition: { duration: 0.15 } },
})({
  zIndex: 2,
  position: 'absolute',
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  transition: 'background .25s ease-out',
  backgroundColor: theme.color.backgroundSecondary,

  '&[data-viewport="desktop"]': {
    backgroundColor: theme.color.backgroundLightDark,
  },
});

const Boundary = styled.div({
  width: '100%',
  transition: 'background .25s ease-out, max-width .25s ease-out',
  overflow: 'hidden',
  border: `${pxToRem(16)} solid black`,

  '[data-viewport="desktop"] &': {
    maxWidth: pxToRem(1024),
    border: 'none',
  },

  '[data-viewport="mobile"] &': {
    borderRadius: pxToRem(32),
    maxWidth: pxToRem(375),
  },

  '[data-viewport="tablet"] &': {
    borderRadius: pxToRem(32),
    maxWidth: pxToRem(768),
  },
});

const FormContainer = styled.div({
  backgroundColor: theme.color.backgroundLightDark,
  height: '100%',
  width: '100%',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  alignItems: 'center',
  transition: 'all .25s ease-out',
  padding: `${pxToRem(40)} ${pxToRem(24)}`,

  '[data-viewport="tablet"] &': {
    padding: pxToRem(56),
  },
});
